import React, { CSSProperties, useEffect, useMemo, useRef } from 'react';
import { FaChevronDown } from 'react-icons/fa6';
import { IoMdClose } from 'react-icons/io';
import { CustomSelectProps } from '../../types';
import { sleep } from '../../helpers/utils';
import './CustomSelect.scss';

const CustomSelect = ({
  placeholder,
  id = Date.now().toString() + Math.random().toString(36).substr(2),
  value,
  label,
  height,
  width = '100%',
  name,
  disabled = false,
  prefix,
  suffix,
  options,
  disabledOptions = [],
  optionLabel,
  optionValue,
  className,
  prefixOnClick,
  multiple,
  searchable,
  itemIcon,
  ...props
}: CustomSelectProps) => {
  const styles: CSSProperties = {
    height,
    width,
  };
  const triggerRef = useRef(null);
  const optionRef = useRef(null);

  const [isActive, setIsActive] = React.useState(false);

  const toggleShow = (which: 'show' | 'hide' | '') => {
    if (disabled) return;

    const select = triggerRef.current as any;
    setIsActive(which === 'show');
    const content = optionRef.current as any;

    if (which === 'show') {
      select.classList.add('active');
      document.body.style.overflow = 'hidden';
      content.style.display = 'block';
    } else {
      select.classList.remove('active');
      document.body.style.overflow = 'auto';
      content.style.display = 'none';
    }
  };

  const [inputValue, setInputValue] = React.useState('');
  const selectedValue = useMemo(() => {
    let result = [];
    if (value && value?.length && multiple) {
      result = options.filter((option) => value.includes(optionValue ? option[optionValue] : option));
    }

    // put the disabled options at the end
    result = result.sort((a, b) => {
      if (disabledOptions.includes(optionValue ? a[optionValue] : a)) {
        return 1;
      }
      if (disabledOptions.includes(optionValue ? b[optionValue] : b)) {
        return -1;
      }
      return 0;
    });

    return result;
  }, [value]);

  useEffect(() => {
    if (!multiple) {
      // check if value is an array and it is empty
      if ((value && Array.isArray(value) && value.length === 0) || (!value && value !== '')) {
        setInputValue('');
        return;
      }

      if (value && inputValue !== value) {
        const val = optionValue ? options?.find((option) => option[optionValue] === value) : undefined;
        setInputValue(!optionValue ? value : val ? val[optionLabel ?? ''] : value);
        return;
      }

      // check options if there is any value that has empty string
      if (options && options.length && value === '') {
        const hasEmptyString = options.find((option) => (!optionValue ? option === '' : option[optionValue] === ''));
        if (hasEmptyString) {
          setInputValue(hasEmptyString[optionLabel ?? '']);
        } else {
          setInputValue(value);
        }
      }
    }
  }, [value]);

  const selectOption = (option: any) => {
    toggleShow('hide');

    if (!multiple) {
      setInputValue(optionLabel ? option[optionLabel] : option);
      props.onChange && props.onChange(optionValue ? option[optionValue] : option);
    } else {
      const selected = selectedValue;
      const index = selected.findIndex((opt) =>
        optionValue ? opt[optionValue] === option[optionValue] : opt === option,
      );
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(option);
      }
      props.onChange && props.onChange(selected.map((opt) => (optionValue ? opt[optionValue] : opt)));
    }
  };

  const [inputFocused, setInputFocused] = React.useState(false);
  const [filteredOptions, setFilterOptions] = React.useState(options);

  useEffect(() => {
    setFilterOptions([]);
  }, [inputFocused]);

  // on input change
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (inputFocused) {
      // filter options
      if (options && options.length) {
        const filteredOptions = options.filter((option) => {
          const optionValue = optionLabel ? option[optionLabel] : option;
          return optionValue.toLowerCase().includes(e.target.value.toLowerCase());
        });
        setFilterOptions(filteredOptions);
        if (filteredOptions.length) {
          toggleShow('show');
          return;
        } else {
          toggleShow('hide');
        }
      }
    }
  };

  const formatVal = (val: any) => {
    return typeof val === 'object' ? JSON.stringify(val) : val;
  };

  const selectedIcon = (val: any) => {
    // search for the icon in the options
    if (options && options.length) {
      const selectedOption = options.find((opt) => (optionValue ? opt[optionValue] === val : opt === val));
      if (selectedOption) {
        if (itemIcon) {
          if (typeof itemIcon === 'string') {
            return <img className="select-component__area-input-icon" src={selectedOption[itemIcon]} alt="icon" />;
          } else {
            return itemIcon;
          }
        }
      }
    }
    return null;
  };

  const checkIsSelected = (option: any) => {
    if (!multiple) return optionValue ? option[optionValue] === value : option === value;
    return selectedValue.find((opt) => (optionValue ? opt[optionValue] === option[optionValue] : opt === option));
  };

  const unselectOption = (option: any) => {
    const selected = selectedValue;
    const index = selected.findIndex((opt) =>
      optionValue ? opt[optionValue] === option[optionValue] : opt === option,
    );
    if (index > -1) {
      selected.splice(index, 1);
    }
    props.onChange && props.onChange(selected.map((opt) => (optionValue ? opt[optionValue] : opt)));
  };

  const handleFocus = async () => {
    if (document.body.style.overflow === 'hidden') {
      await sleep(100);
      document.body.style.overflow = 'hidden';
    }
  };

  const availableOptions = useMemo(() => {
    return (filteredOptions.length > 0 && inputFocused ? filteredOptions : options).filter(
      (option) => !disabledOptions.includes(optionValue ? option[optionValue] : option),
    );
  }, [filteredOptions, options, disabledOptions]);
  return (
    <div className={`select-component ${className}`}>
      <label className="select-component__label" htmlFor={id}>
        {label}
      </label>
      <div
        style={styles}
        className={`select-component__area  ${multiple && 'multiple'}`}
        ref={triggerRef}
        onClick={() => {
          if (disabled) return;
        }}
      >
        <div
          className={`select-component__area-prefix" ${prefixOnClick && !disabled && 'clickable'}`}
          onClick={prefixOnClick && prefixOnClick}
        >
          {prefix}
        </div>
        {multiple ? (
          <div className="selected-values" onClick={() => toggleShow('show')}>
            {/* selectedValue lenght is 0 and placeholder value exist */}
            {selectedValue.length === 0 && placeholder && (
              <span className="selected-values__item-placeholder">{placeholder}</span>
            )}

            {selectedValue.map((option, index) => (
              <span
                key={index}
                className={`selected-values__item ${disabledOptions.includes(optionValue ? option[optionValue] : option) && 'selected-values__item--disabled'}`}
              >
                {itemIcon && (
                  <>
                    {typeof itemIcon === 'string' ? (
                      <img className="selected-values__item-icon" src={option[itemIcon]} alt="selected icon" />
                    ) : (
                      itemIcon
                    )}
                  </>
                )}

                {formatVal(optionLabel ? option[optionLabel] : option)}

                <IoMdClose
                  className="selected-values__item-close"
                  size={10}
                  onClick={(e) => {
                    e.stopPropagation();
                    unselectOption(option);
                  }}
                />
              </span>
            ))}
          </div>
        ) : (
          <>
            {searchable ? (
              <input
                onFocus={() => {
                  handleFocus();
                  setInputFocused(true);
                  toggleShow('show');
                }}
                onBlur={() => setInputFocused(false)}
                id={id}
                disabled={disabled}
                {...props}
                value={formatVal(inputValue)}
                {...props}
                name={name}
                placeholder={placeholder}
                onChange={onInputChange}
                className={`${!disabled && 'clickable'}`}
              />
            ) : (
              <div
                className={`select-component__area-input ${!disabled && 'clickable'}`}
                onClick={() => {
                  toggleShow('show');
                }}
              >
                {selectedIcon(value)}
                <span
                  className="select-component__area-input-text"
                  style={{
                    color: inputValue ? '#212337' : '',
                  }}
                >
                  {formatVal(inputValue) || placeholder}
                </span>
              </div>
            )}
          </>
        )}
        {!disabled && (
          <>
            <div
              className={`select-component__area-suffix clickable`}
              onClick={() => {
                toggleShow('show');
              }}
            >
              {suffix || <FaChevronDown />}
            </div>

            <ul className="select-component__area-options" ref={optionRef}>
              {availableOptions.map((option, index) => (
                <li
                  key={index}
                  className={`select-component__area-options-item ${checkIsSelected(option) && 'selected'} ${multiple && 'multiple'}`}
                  onClick={() => selectOption(option)}
                >
                  {itemIcon && (
                    <>
                      {typeof itemIcon === 'string' ? (
                        <img className="selected-values__item-icon" src={option[itemIcon]} alt="selected icon" />
                      ) : (
                        itemIcon
                      )}
                    </>
                  )}
                  <span className="select-text" title={formatVal(optionLabel ? option[optionLabel] : option)}>
                    {formatVal(optionLabel ? option[optionLabel] : option)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {isActive && <div className="select-component__overlay" onClick={() => toggleShow('hide')} />}
    </div>
  );
};

export default CustomSelect;
